import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { unbilledTimeAPI, timeEntryAPI, customerAPI, projectAPI } from '../services/api';
import { 
  UnbilledEntriesResponse, 
  UnbilledEntriesGroup, 
  UnbilledEntry,
  Customer,
  Project
} from '../types';

interface User {
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
}

type GroupBy = 'customer' | 'project' | 'none';

const UnbilledEntriesReport: React.FC = () => {
  const [data, setData] = useState<UnbilledEntriesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Filters
  const [groupBy, setGroupBy] = useState<GroupBy>(() => {
    return (localStorage.getItem('unbilled_groupBy') as GroupBy) || 'customer';
  });
  const [customerId, setCustomerId] = useState<string>('');
  const [projectId, setProjectId] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  
  // Filter options
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [userId, setUserId] = useState<string>('');
  
  // Selection for bulk actions
  const [selectedEntries, setSelectedEntries] = useState<Set<number>>(new Set());
  
  // Write-off dialog (single entry)
  const [writeOffEntry, setWriteOffEntry] = useState<UnbilledEntry | null>(null);
  const [writeOffReason, setWriteOffReason] = useState('');
  const [writeOffLoading, setWriteOffLoading] = useState(false);
  
  // Bulk write-off dialog
  const [showBulkWriteOff, setShowBulkWriteOff] = useState(false);
  const [bulkWriteOffReason, setBulkWriteOffReason] = useState('');
  const [bulkWriteOffLoading, setBulkWriteOffLoading] = useState(false);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchEntries();
  }, [groupBy, customerId, projectId, userId, startDate, endDate]);

  useEffect(() => {
    localStorage.setItem('unbilled_groupBy', groupBy);
  }, [groupBy]);

  const fetchFilterOptions = async () => {
    try {
      const [customersRes, projectsRes, usersRes] = await Promise.all([
        customerAPI.getAll(),
        projectAPI.getAll(),
        api.get<{ results: User[] }>('/auth/users/')
      ]);
      setCustomers(customersRes.data.results);
      setProjects(projectsRes.data.results);
      setUsers(usersRes.data.results);
    } catch (err) {
      console.error('Failed to fetch filter options:', err);
    }
  };

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setSelectedEntries(new Set()); // Clear selection when data changes
      const params: any = {};
      if (groupBy !== 'none') params.group_by = groupBy;
      if (customerId) params.customer_id = parseInt(customerId);
      if (projectId) params.project_id = parseInt(projectId);
      if (userId) params.user_id = parseInt(userId);
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      
      const response = await unbilledTimeAPI.getEntries(params);
      setData(response.data);
      setError(null);
    } catch (err) {
      setError('Failed to load unbilled entries');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleWriteOff = async () => {
    if (!writeOffEntry) return;
    
    try {
      setWriteOffLoading(true);
      await timeEntryAPI.writeOff(writeOffEntry.id, writeOffReason);
      setWriteOffEntry(null);
      setWriteOffReason('');
      fetchEntries(); // Refresh the list
    } catch (err) {
      console.error('Failed to write off entry:', err);
      alert('Failed to write off entry');
    } finally {
      setWriteOffLoading(false);
    }
  };

  const handleBulkWriteOff = async () => {
    if (selectedEntries.size === 0) return;
    
    try {
      setBulkWriteOffLoading(true);
      const result = await timeEntryAPI.bulkWriteOff(Array.from(selectedEntries), bulkWriteOffReason);
      setShowBulkWriteOff(false);
      setBulkWriteOffReason('');
      setSelectedEntries(new Set());
      
      // Show success message
      const writtenOff = result.data.written_off_count || 0;
      const skipped = result.data.skipped_count || 0;
      
      let message = `Successfully wrote off ${writtenOff} ${writtenOff === 1 ? 'entry' : 'entries'}.`;
      if (skipped > 0) {
        message += `\n\n${skipped} ${skipped === 1 ? 'entry was' : 'entries were'} skipped (already written off or already on an invoice).`;
      }
      
      window.alert(message);
      
      fetchEntries(); // Refresh the list
    } catch (err) {
      console.error('Failed to bulk write off entries:', err);
      alert('Failed to write off entries');
    } finally {
      setBulkWriteOffLoading(false);
    }
  };

  // Selection helpers
  const toggleEntrySelection = (entryId: number) => {
    setSelectedEntries(prev => {
      const next = new Set(prev);
      if (next.has(entryId)) {
        next.delete(entryId);
      } else {
        next.add(entryId);
      }
      return next;
    });
  };

  const getAllVisibleEntryIds = (): number[] => {
    if (!data) return [];
    if (data.grouped && data.groups) {
      return data.groups.flatMap(g => g.entries.map(e => e.id));
    }
    if (data.entries) {
      return data.entries.map(e => e.id);
    }
    return [];
  };

  const toggleSelectAll = () => {
    const allIds = getAllVisibleEntryIds();
    if (selectedEntries.size === allIds.length && allIds.length > 0) {
      setSelectedEntries(new Set());
    } else {
      setSelectedEntries(new Set(allIds));
    }
  };

  const getSelectedSummary = () => {
    if (!data || selectedEntries.size === 0) return { hours: 0, amount: 0 };
    
    let allEntries: UnbilledEntry[] = [];
    if (data.grouped && data.groups) {
      allEntries = data.groups.flatMap(g => g.entries);
    } else if (data.entries) {
      allEntries = data.entries;
    }
    
    return allEntries
      .filter(e => selectedEntries.has(e.id))
      .reduce((acc, e) => ({
        hours: acc.hours + e.hours,
        amount: acc.amount + e.amount
      }), { hours: 0, amount: 0 });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString + 'T00:00:00').toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getAgingStyle = (agingLevel: string) => {
    switch (agingLevel) {
      case 'danger':
        return { backgroundColor: '#fce4e4', color: '#c0392b' };
      case 'warning':
        return { backgroundColor: '#fef3e2', color: '#d35400' };
      default:
        return {};
    }
  };

  const renderEntry = (entry: UnbilledEntry) => (
    <tr 
      key={entry.id} 
      style={{ 
        borderBottom: '1px solid #f0f0f0',
        ...getAgingStyle(entry.aging_level)
      }}
    >
      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
        <input
          type="checkbox"
          checked={selectedEntries.has(entry.id)}
          onChange={() => toggleEntrySelection(entry.id)}
          style={{ cursor: 'pointer' }}
        />
      </td>
      <td style={{ padding: '0.5rem 0.75rem' }}>{formatDate(entry.date)}</td>
      <td style={{ padding: '0.5rem 0.75rem' }}>{entry.user_name}</td>
      <td style={{ padding: '0.5rem 0.75rem' }}>{entry.project_code}</td>
      <td style={{ padding: '0.5rem 0.75rem' }}>{entry.billing_rate_name}</td>
      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{entry.hours.toFixed(2)}</td>
      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{formatCurrency(entry.rate_amount)}</td>
      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(entry.amount)}</td>
      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
        {entry.days_old > 60 ? (
          <span style={{ 
            display: 'inline-block',
            padding: '0.15rem 0.4rem',
            backgroundColor: '#c0392b',
            color: 'white',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: 500
          }}>
            {entry.days_old}d
          </span>
        ) : entry.days_old > 30 ? (
          <span style={{ 
            display: 'inline-block',
            padding: '0.15rem 0.4rem',
            backgroundColor: '#d35400',
            color: 'white',
            borderRadius: '4px',
            fontSize: '0.7rem',
            fontWeight: 500
          }}>
            {entry.days_old}d
          </span>
        ) : (
          <span style={{ fontSize: '0.8rem', color: '#666' }}>{entry.days_old}d</span>
        )}
      </td>
      <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
        <button
          onClick={() => setWriteOffEntry(entry)}
          className="btn btn-secondary"
          style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
        >
          Write Off
        </button>
      </td>
    </tr>
  );

  const renderGroupedEntries = () => {
    if (!data?.groups) return null;
    
    return data.groups.map((group: UnbilledEntriesGroup) => (
      <div key={group.group_name} style={{ marginBottom: '2rem' }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          padding: '0.75rem 1rem',
          backgroundColor: '#2c3e50',
          color: 'white',
          borderRadius: '8px 8px 0 0'
        }}>
          <div>
            <strong>{group.group_name}</strong>
            <span style={{ marginLeft: '1rem', opacity: 0.8, fontSize: '0.9rem' }}>
              {group.entries.length} entries
            </span>
          </div>
          <div>
            <span style={{ marginRight: '1.5rem' }}>{group.total_hours.toFixed(1)} hrs</span>
            <strong>{formatCurrency(group.total_amount)}</strong>
          </div>
        </div>
        <div style={{ border: '1px solid #e0e0e0', borderTop: 'none', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa' }}>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', width: '40px' }}>
                  <input
                    type="checkbox"
                    checked={group.entries.length > 0 && group.entries.every(e => selectedEntries.has(e.id))}
                    onChange={() => {
                      const groupIds = group.entries.map(e => e.id);
                      const allSelected = groupIds.every(id => selectedEntries.has(id));
                      setSelectedEntries(prev => {
                        const next = new Set(prev);
                        groupIds.forEach(id => allSelected ? next.delete(id) : next.add(id));
                        return next;
                      });
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                </th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>Date</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>Employee</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>Project</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>Rate</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Hours</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Rate/Hr</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Amount</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>Age</th>
                <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {group.entries.map(renderEntry)}
            </tbody>
          </table>
        </div>
      </div>
    ));
  };

  const renderFlatEntries = () => {
    if (!data?.entries) return null;
    
    const allIds = data.entries.map(e => e.id);
    const allSelected = allIds.length > 0 && allIds.every(id => selectedEntries.has(id));
    
    return (
      <div style={{ border: '1px solid #e0e0e0', borderRadius: '8px', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center', width: '40px' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  style={{ cursor: 'pointer' }}
                />
              </th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>Date</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>Customer</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>Employee</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>Project</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'left' }}>Rate</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Hours</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Rate/Hr</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Amount</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>Age</th>
              <th style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>Action</th>
            </tr>
          </thead>
          <tbody>
            {data.entries.map((entry) => (
              <tr 
                key={entry.id} 
                style={{ 
                  borderBottom: '1px solid #f0f0f0',
                  ...getAgingStyle(entry.aging_level)
                }}
              >
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                  <input
                    type="checkbox"
                    checked={selectedEntries.has(entry.id)}
                    onChange={() => toggleEntrySelection(entry.id)}
                    style={{ cursor: 'pointer' }}
                  />
                </td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{formatDate(entry.date)}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{entry.customer_name}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{entry.user_name}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{entry.project_code}</td>
                <td style={{ padding: '0.5rem 0.75rem' }}>{entry.billing_rate_name}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{entry.hours.toFixed(2)}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>{formatCurrency(entry.rate_amount)}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right', fontWeight: 500 }}>{formatCurrency(entry.amount)}</td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'center' }}>
                  {entry.days_old > 60 ? (
                    <span style={{ 
                      display: 'inline-block',
                      padding: '0.15rem 0.4rem',
                      backgroundColor: '#c0392b',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}>
                      {entry.days_old}d
                    </span>
                  ) : entry.days_old > 30 ? (
                    <span style={{ 
                      display: 'inline-block',
                      padding: '0.15rem 0.4rem',
                      backgroundColor: '#d35400',
                      color: 'white',
                      borderRadius: '4px',
                      fontSize: '0.7rem',
                      fontWeight: 500
                    }}>
                      {entry.days_old}d
                    </span>
                  ) : (
                    <span style={{ fontSize: '0.8rem', color: '#666' }}>{entry.days_old}d</span>
                  )}
                </td>
                <td style={{ padding: '0.5rem 0.75rem', textAlign: 'right' }}>
                  <button
                    onClick={() => setWriteOffEntry(entry)}
                    className="btn btn-secondary"
                    style={{ fontSize: '0.7rem', padding: '0.2rem 0.4rem' }}
                  >
                    Write Off
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="container">
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0 }}>Unbilled Time Report</h2>
          <Link to="/invoices/new" className="btn btn-primary">
            Create Invoice
          </Link>
        </div>

        {/* Filters */}
        <div style={{ 
          display: 'flex', 
          flexWrap: 'wrap',
          gap: '1rem', 
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px'
        }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
              Group By
            </label>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setGroupBy('customer')}
                className={`btn ${groupBy === 'customer' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
              >
                Customer
              </button>
              <button
                onClick={() => setGroupBy('project')}
                className={`btn ${groupBy === 'project' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
              >
                Project
              </button>
              <button
                onClick={() => setGroupBy('none')}
                className={`btn ${groupBy === 'none' ? 'btn-primary' : 'btn-secondary'}`}
                style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
              >
                None
              </button>
            </div>
          </div>

          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
              Customer
            </label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              style={{ width: '100%', padding: '0.4rem', fontSize: '0.9rem' }}
            >
              <option value="">All Customers</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
              Project
            </label>
            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              style={{ width: '100%', padding: '0.4rem', fontSize: '0.9rem' }}
            >
              <option value="">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>{p.code} - {p.name}</option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: '150px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
              Employee
            </label>
            <select
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              style={{ width: '100%', padding: '0.4rem', fontSize: '0.9rem' }}
            >
              <option value="">All Employees</option>
              {users.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.first_name && u.last_name 
                    ? `${u.first_name} ${u.last_name}` 
                    : u.username}
                </option>
              ))}
            </select>
          </div>

          <div style={{ minWidth: '130px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{ width: '100%', padding: '0.4rem', fontSize: '0.9rem' }}
            />
          </div>

          <div style={{ minWidth: '130px' }}>
            <label style={{ display: 'block', fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem' }}>
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{ width: '100%', padding: '0.4rem', fontSize: '0.9rem' }}
            />
          </div>

          {(customerId || projectId || userId || startDate || endDate) && (
            <div style={{ alignSelf: 'flex-end' }}>
              <button
                onClick={() => {
                  setCustomerId('');
                  setProjectId('');
                  setUserId('');
                  setStartDate('');
                  setEndDate('');
                }}
                className="btn btn-secondary"
                style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
              >
                Clear Filters
              </button>
            </div>
          )}
        </div>

        {/* Summary stats */}
        {data && (
          <div style={{ 
            display: 'flex', 
            gap: '2rem',
            marginBottom: '1.5rem',
            padding: '1rem',
            backgroundColor: '#e8f5e9',
            borderRadius: '8px',
            border: '1px solid #c8e6c9'
          }}>
            <div>
              <span style={{ color: '#666', marginRight: '0.5rem' }}>Total Entries:</span>
              <strong>{data.total_entries}</strong>
            </div>
            <div>
              <span style={{ color: '#666', marginRight: '0.5rem' }}>Total Hours:</span>
              <strong>{data.total_hours.toFixed(1)}</strong>
            </div>
            <div>
              <span style={{ color: '#666', marginRight: '0.5rem' }}>Total Amount:</span>
              <strong style={{ color: '#27ae60' }}>{formatCurrency(data.total_amount)}</strong>
            </div>
          </div>
        )}

        {/* Bulk Action Toolbar */}
        {selectedEntries.size > 0 && (
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            gap: '1rem',
            marginBottom: '1rem',
            padding: '0.75rem 1rem',
            backgroundColor: '#fff3cd',
            borderRadius: '8px',
            border: '1px solid #ffc107'
          }}>
            <span style={{ fontWeight: 500 }}>
              {selectedEntries.size} {selectedEntries.size === 1 ? 'entry' : 'entries'} selected
            </span>
            <span style={{ color: '#666', fontSize: '0.9rem' }}>
              ({getSelectedSummary().hours.toFixed(1)} hrs / {formatCurrency(getSelectedSummary().amount)})
            </span>
            <button
              onClick={() => setShowBulkWriteOff(true)}
              className="btn btn-primary"
              style={{ backgroundColor: '#e74c3c', fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
            >
              Write Off Selected
            </button>
            <button
              onClick={() => setSelectedEntries(new Set())}
              className="btn btn-secondary"
              style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem' }}
            >
              Clear Selection
            </button>
          </div>
        )}

        {/* Content */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#666' }}>
            Loading unbilled entries...
          </div>
        )}

        {error && (
          <div style={{ textAlign: 'center', padding: '3rem', color: '#e74c3c' }}>
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <>
            {data.total_entries === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: '#27ae60' }}>
                <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>✓</div>
                <div style={{ fontSize: '1.2rem' }}>All billable time has been invoiced!</div>
              </div>
            ) : (
              data.grouped ? renderGroupedEntries() : renderFlatEntries()
            )}
          </>
        )}
      </div>

      {/* Write-off Modal */}
      {writeOffEntry && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Write Off Time Entry</h3>
            
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px' }}>
              <div><strong>{formatDate(writeOffEntry.date)}</strong> - {writeOffEntry.user_name}</div>
              <div style={{ fontSize: '0.9rem', color: '#666', marginTop: '0.25rem' }}>
                {writeOffEntry.project_code} - {writeOffEntry.billing_rate_name}
              </div>
              <div style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
                {writeOffEntry.hours} hours × {formatCurrency(writeOffEntry.rate_amount)} = <strong>{formatCurrency(writeOffEntry.amount)}</strong>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="writeoff-reason" style={{ display: 'block', marginBottom: '0.25rem' }}>
                Reason (optional)
              </label>
              <textarea
                id="writeoff-reason"
                value={writeOffReason}
                onChange={(e) => setWriteOffReason(e.target.value)}
                placeholder="Why is this time not being billed?"
                rows={3}
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setWriteOffEntry(null);
                  setWriteOffReason('');
                }}
                className="btn btn-secondary"
                disabled={writeOffLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleWriteOff}
                className="btn btn-primary"
                disabled={writeOffLoading}
                style={{ backgroundColor: '#e74c3c' }}
              >
                {writeOffLoading ? 'Writing Off...' : 'Write Off'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Write-off Modal */}
      {showBulkWriteOff && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '8px',
            padding: '1.5rem',
            maxWidth: '500px',
            width: '90%',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}>
            <h3 style={{ margin: '0 0 1rem 0' }}>Write Off Multiple Time Entries</h3>
            
            <div style={{ marginBottom: '1rem', padding: '1rem', backgroundColor: '#fff3cd', borderRadius: '4px', border: '1px solid #ffc107' }}>
              <div style={{ fontWeight: 500, marginBottom: '0.5rem' }}>
                You are about to write off {selectedEntries.size} {selectedEntries.size === 1 ? 'entry' : 'entries'}
              </div>
              <div style={{ fontSize: '0.9rem' }}>
                <span style={{ marginRight: '1rem' }}>
                  <strong>{getSelectedSummary().hours.toFixed(1)}</strong> total hours
                </span>
                <span>
                  <strong>{formatCurrency(getSelectedSummary().amount)}</strong> total value
                </span>
              </div>
            </div>

            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label htmlFor="bulk-writeoff-reason" style={{ display: 'block', marginBottom: '0.25rem' }}>
                Reason (optional - applies to all selected entries)
              </label>
              <textarea
                id="bulk-writeoff-reason"
                value={bulkWriteOffReason}
                onChange={(e) => setBulkWriteOffReason(e.target.value)}
                placeholder="Why is this time not being billed?"
                rows={3}
                style={{ width: '100%', padding: '0.5rem' }}
              />
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowBulkWriteOff(false);
                  setBulkWriteOffReason('');
                }}
                className="btn btn-secondary"
                disabled={bulkWriteOffLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleBulkWriteOff}
                className="btn btn-primary"
                disabled={bulkWriteOffLoading}
                style={{ backgroundColor: '#e74c3c' }}
              >
                {bulkWriteOffLoading 
                  ? 'Writing Off...' 
                  : `Write Off ${selectedEntries.size} ${selectedEntries.size === 1 ? 'Entry' : 'Entries'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnbilledEntriesReport;


